import React, { useState } from "react";
import LanguageSelector from "./LanguageSelector";

const MobileNav = ({
  menuData,
  ctaButtons,
  mobileMenuOpen,
  setMobileMenuOpen,
  homeUrl,
  searchIcon,
  isScrolled,
  languages = [],
}) => {
  const [expandedItems, setExpandedItems] = useState([]);

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  const toggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) {
      setExpandedItems([]);
    }
  };

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="mobile-nav-header flex items-center justify-end px-4 py-3 gap-4">
        {/* Language Selector */}
        <LanguageSelector languages={languages} hasWhiteBackground={true} className="min-w-fit" />

        <button
          onClick={toggleMenu}
          className="relative w-10 h-10 flex items-center justify-center focus:outline-none"
          aria-label="Toggle menu"
        >
          <div className="relative w-6 h-5">
            <span
              className={`absolute top-0 left-0 block w-full h-0.5 bg-gray-800 transition-all duration-300 origin-center ${
                mobileMenuOpen ? "rotate-45 top-1/2 -translate-y-1/2" : ""
              }`}
            />
            <span
              className={`absolute top-1/2 left-0 -translate-y-1/2 block w-full h-0.5 bg-gray-800 transition-all duration-300 ${
                mobileMenuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute bottom-0 left-0 block w-full h-0.5 bg-gray-800 transition-all duration-300 origin-center ${
                mobileMenuOpen ? "-rotate-45 top-1/2 -translate-y-1/2" : ""
              }`}
            />
          </div>
        </button>
      </div>

      {/* Mobile Menu Overlay - positioned same as menu panel */}
      <div
        className={`
          absolute w-full bg-black/20 z-40 transition-opacity duration-300
          ${mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
        `}
        style={{ left: 0, right: 0, top: "100%", height: "100vh" }}
        onClick={toggleMenu}
      />

      {/* Mobile Menu Panel - Slides down from header */}
      <div
        className={`
          absolute left-0 right-0 w-full bg-white z-50 shadow-2xl
          transition-all duration-300 ease-out overflow-hidden
        `}
        style={{
          top: "100%",
          maxHeight: mobileMenuOpen ? "calc(100vh - 80px)" : "0",
          opacity: mobileMenuOpen ? 1 : 0,
        }}
      >
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 80px)" }}
        >
          {/* Main Menu Items */}
          <nav>
            <ul className="m-0 p-0">
              {menuData.map((item) => (
                <li
                  key={item.id}
                  className="list-none border-b border-gray-100"
                >
                  {item.hasSubmenu ? (
                    <>
                      <div className="flex items-center">
                        <a
                          href={item.url}
                          className="flex-1 px-4 py-3 font-medium text-gray-900 hover:bg-gray-50 transition-colors duration-200"
                        >
                          {item.title}
                        </a>
                        <button
                          onClick={() => toggleExpanded(item.id)}
                          className="px-4 py-3 flex items-center justify-center hover:bg-gray-50 transition-colors duration-200"
                          aria-label={`Toggle ${item.title} submenu`}
                        >
                          <span
                            className={`
                            transform transition-transform duration-200
                            ${expandedItems.includes(item.id) ? "rotate-180" : ""}
                          `}
                          >
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </span>
                        </button>
                      </div>

                      {/* Submenu */}
                      <div
                        className={`
                        overflow-hidden transition-all duration-300
                        ${expandedItems.includes(item.id) ? "max-h-[1000px]" : "max-h-0"}
                      `}
                      >
                        <div className="bg-gray-50 px-4">
                          {item.submenu.map((subItem, index) => (
                            <div
                              key={subItem.id}
                              className={`${index > 0 ? "border-t border-gray-200" : ""}`}
                            >
                              {/* Handle button-only sections */}
                              {subItem.type === "buttons" ? (
                                <div className="flex flex-col space-y-2 py-2 ml-4">
                                  {subItem.children &&
                                    subItem.children.map((button) => (
                                      <a
                                        key={button.id}
                                        href={button.url}
                                        className={`
                                      px-4 py-3 text-base font-semibold text-center transition-all duration-200 hover:no-underline block rounded-full
                                      ${
                                        button.style === "ghost"
                                          ? "border-2 border-muotolevy-button text-muotolevy-button hover:bg-muotolevy-button hover:text-white"
                                          : "bg-muotolevy-button text-white hover:bg-muotolevy-yellow hover:text-black"
                                      }
                                    `}
                                      >
                                        {button.title}
                                      </a>
                                    ))}
                                </div>
                              ) : subItem.sections ? (
                                /* Handle sections with multiple subsections */
                                <div className="ml-4">
                                  {subItem.sections.map((section, index) => (
                                    <div
                                      key={section.id}
                                      className={`${index > 0 ? "border-t border-gray-200" : ""}`}
                                    >
                                      <a
                                        href={section.url}
                                        className="block font-medium text-muotolevy-red hover:text-muotolevy-yellow transition-colors duration-200 pb-3 pt-4"
                                      >
                                        {section.title}
                                      </a>
                                      {section.children && (
                                        <ul className="mt-0 ml-4">
                                          {section.children.map(
                                            (child, index) => (
                                              <li
                                                key={child.id}
                                                className={`${index > 0 ? "border-t border-gray-200" : ""}`}
                                              >
                                                <a
                                                  href={child.url}
                                                  className="block text-base text-gray-600 hover:text-muotolevy-red transition-colors duration-200 py-2.5"
                                                >
                                                  {child.title}
                                                </a>
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                /* Handle regular sections */
                                <div className="ml-4">
                                  <a
                                    href={subItem.url}
                                    className="block font-medium text-muotolevy-red hover:text-muotolevy-yellow transition-colors duration-200 py-3 pt-4"
                                  >
                                    {subItem.title}
                                  </a>
                                  {subItem.children && (
                                    <ul className="mt-0 ml-4">
                                      {subItem.children.map((child, index) => (
                                        <li
                                          key={child.id}
                                          className={`${child.type !== "button" && index > 0 ? "border-t border-gray-200" : ""}`}
                                        >
                                          {child.type === "button" ? (
                                            <a
                                              href={child.url}
                                              className={`
                                              px-4 py-3 text-base font-semibold text-center transition-all duration-200 hover:no-underline block rounded-full mt-2
                                              ${
                                                child.style === "ghost"
                                                  ? "border-2 border-muotolevy-button text-muotolevy-button hover:bg-muotolevy-button hover:text-white"
                                                  : "bg-muotolevy-button text-white hover:bg-muotolevy-yellow hover:text-black"
                                              }
                                            `}
                                            >
                                              {child.title}
                                            </a>
                                          ) : (
                                            <a
                                              href={child.url}
                                              className="block text-base text-gray-600 hover:text-muotolevy-red transition-colors duration-200 py-2.5"
                                            >
                                              {child.title}
                                            </a>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <a
                      href={item.url}
                      className="block px-4 py-3 font-medium text-gray-900 hover:bg-gray-50 transition-colors duration-200"
                    >
                      {item.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default MobileNav;
